/**
 * BlockDetail - Modal for editing a time block
 *
 * Features:
 * - Subject selector
 * - Time range (start/end)
 * - Curriculum outcome picker
 * - Resource attachment (worksheets, PDFs)
 * - Notes field
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Clock,
  BookOpen,
  FileText,
  Target,
  Trash2,
  Plus,
  Search,
  X,
  Save,
} from "lucide-react";
import {
  type TimeBlock,
  type Subject,
  type CurriculumOutcome,
  type DayOfWeek,
  SUBJECTS,
  SUBJECT_DOT_COLORS,
  formatTime,
} from "./types";

// ============================================================================
// Types
// ============================================================================

export interface BlockDetailProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Block being edited (null for new block) */
  block: TimeBlock | null;
  /** Day of the week for new blocks */
  day?: DayOfWeek;
  /** Default start time for new blocks */
  defaultStartTime?: string;
  /** Default end time for new blocks */
  defaultEndTime?: string;
  /** Available curriculum outcomes */
  outcomes: CurriculumOutcome[];
  /** Called when block is saved */
  onSave: (block: Omit<TimeBlock, "id"> & { id?: string }) => void;
  /** Called when block is deleted */
  onDelete?: (blockId: string) => void;
}

// ============================================================================
// Sub-Components
// ============================================================================

interface OutcomePickerProps {
  outcomes: CurriculumOutcome[];
  selectedIds: string[];
  onToggle: (outcomeId: string) => void;
}

function OutcomePicker({ outcomes, selectedIds, onToggle }: OutcomePickerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [subjectFilter, setSubjectFilter] = useState<string>("all");
  const [gradeFilter, setGradeFilter] = useState<string>("all");

  // Get unique subjects and grades
  const subjects = useMemo(() => {
    const unique = new Set(outcomes.map((o) => o.subject));
    return Array.from(unique).sort();
  }, [outcomes]);

  const grades = useMemo(() => {
    const unique = new Set(outcomes.map((o) => o.grade));
    return Array.from(unique).sort((a, b) => {
      if (a === "Kindergarten") return -1;
      if (b === "Kindergarten") return 1;
      const numA = parseInt(a.replace("Grade ", ""));
      const numB = parseInt(b.replace("Grade ", ""));
      return numA - numB;
    });
  }, [outcomes]);

  // Filter outcomes
  const filteredOutcomes = useMemo(() => {
    return outcomes.filter((outcome) => {
      // Subject filter
      if (subjectFilter !== "all" && outcome.subject !== subjectFilter) {
        return false;
      }
      // Grade filter
      if (gradeFilter !== "all" && outcome.grade !== gradeFilter) {
        return false;
      }
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          outcome.outcome_text.toLowerCase().includes(query) ||
          outcome.organizing_idea.toLowerCase().includes(query) ||
          outcome.id.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [outcomes, subjectFilter, gradeFilter, searchQuery]);

  // Group by organizing idea
  const groupedOutcomes = useMemo(() => {
    const groups = new Map<string, CurriculumOutcome[]>();
    for (const outcome of filteredOutcomes) {
      const key = `${outcome.subject} - ${outcome.organizing_idea}`;
      const group = groups.get(key) || [];
      group.push(outcome);
      groups.set(key, group);
    }
    return groups;
  }, [filteredOutcomes]);

  return (
    <div className="space-y-3">
      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search outcomes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={subjectFilter} onValueChange={setSubjectFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue placeholder="Subject" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Subjects</SelectItem>
            {subjects.map((subject) => (
              <SelectItem key={subject} value={subject}>
                {subject}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={gradeFilter} onValueChange={setGradeFilter}>
          <SelectTrigger className="w-full sm:w-[120px]">
            <SelectValue placeholder="Grade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Grades</SelectItem>
            {grades.map((grade) => (
              <SelectItem key={grade} value={grade}>
                {grade}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Selected count */}
      {selectedIds.length > 0 && (
        <div className="text-sm text-muted-foreground">
          {selectedIds.length} outcome{selectedIds.length !== 1 ? "s" : ""} selected
        </div>
      )}

      {/* Outcomes list */}
      <ScrollArea className="h-[200px] border rounded-md">
        <div className="p-2 space-y-4">
          {filteredOutcomes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No outcomes found
            </div>
          ) : (
            Array.from(groupedOutcomes.entries()).map(([groupName, outcomes]) => (
              <div key={groupName}>
                <div className="text-xs font-medium text-muted-foreground mb-2 px-1">
                  {groupName}
                </div>
                <div className="space-y-1">
                  {outcomes.map((outcome) => (
                    <label
                      key={outcome.id}
                      className={cn(
                        "flex items-start gap-2 p-2 rounded-md cursor-pointer",
                        "hover:bg-muted transition-colors",
                        selectedIds.includes(outcome.id) && "bg-muted"
                      )}
                    >
                      <Checkbox
                        checked={selectedIds.includes(outcome.id)}
                        onCheckedChange={() => onToggle(outcome.id)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {outcome.id}
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-2">
                          {outcome.outcome_text}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function BlockDetail({
  open,
  onOpenChange,
  block,
  day,
  defaultStartTime = "08:45",
  defaultEndTime = "09:30",
  outcomes,
  onSave,
  onDelete,
}: BlockDetailProps) {
  // Form state
  const [subject, setSubject] = useState<Subject | "">("");
  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);
  const [notes, setNotes] = useState("");
  const [selectedOutcomeIds, setSelectedOutcomeIds] = useState<string[]>([]);
  const [outcomePickerOpen, setOutcomePickerOpen] = useState(false);

  // Track block identity to avoid resetting form on defaultStartTime/defaultEndTime changes
  const prevBlockIdRef = useRef<string | null | undefined>(undefined);

  // Initialize form when block identity changes (not when defaultStartTime/defaultEndTime change)
  useEffect(() => {
    const currentBlockId = block?.id ?? null;

    // Only reset form when block identity changes
    if (prevBlockIdRef.current !== currentBlockId) {
      if (block) {
        setSubject(block.subject || "");
        setTitle(block.title);
        setStartTime(block.startTime);
        setEndTime(block.endTime);
        setNotes(block.notes);
        setSelectedOutcomeIds(block.outcomeIds);
      } else {
        // Reset for new block
        setSubject("");
        setTitle("");
        setStartTime(defaultStartTime);
        setEndTime(defaultEndTime);
        setNotes("");
        setSelectedOutcomeIds([]);
      }
      prevBlockIdRef.current = currentBlockId;
    }
  }, [block, defaultStartTime, defaultEndTime]);

  // Handle outcome toggle
  const handleOutcomeToggle = useCallback((outcomeId: string) => {
    setSelectedOutcomeIds((prev) =>
      prev.includes(outcomeId)
        ? prev.filter((id) => id !== outcomeId)
        : [...prev, outcomeId]
    );
  }, []);

  // Remove outcome
  const handleRemoveOutcome = useCallback((outcomeId: string) => {
    setSelectedOutcomeIds((prev) => prev.filter((id) => id !== outcomeId));
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    const blockData: Omit<TimeBlock, "id"> & { id?: string } = {
      id: block?.id,
      day: block?.day || day || "Monday",
      startTime,
      endTime,
      subject: subject || null,
      title,
      notes,
      outcomeIds: selectedOutcomeIds,
      resources: block?.resources || [],
    };
    onSave(blockData);
    onOpenChange(false);
  }, [
    block,
    day,
    startTime,
    endTime,
    subject,
    title,
    notes,
    selectedOutcomeIds,
    onSave,
    onOpenChange,
  ]);

  // Handle delete
  const handleDelete = useCallback(() => {
    if (block?.id && onDelete) {
      onDelete(block.id);
      onOpenChange(false);
    }
  }, [block, onDelete, onOpenChange]);

  // Get selected outcomes data
  const selectedOutcomes = useMemo(() => {
    const outcomesMap = new Map(outcomes.map((o) => [o.id, o]));
    return selectedOutcomeIds
      .map((id) => outcomesMap.get(id))
      .filter((o): o is CurriculumOutcome => o !== undefined);
  }, [outcomes, selectedOutcomeIds]);

  const isNewBlock = !block?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isNewBlock ? "Add Time Block" : "Edit Time Block"}
          </DialogTitle>
          <DialogDescription>
            {isNewBlock
              ? "Create a new time block for your schedule."
              : "Update the details of this time block."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Select
              value={subject}
              onValueChange={(value) => setSubject(value as Subject)}
            >
              <SelectTrigger id="subject">
                <SelectValue placeholder="Select a subject..." />
              </SelectTrigger>
              <SelectContent>
                {SUBJECTS.map((subj) => (
                  <SelectItem key={subj} value={subj}>
                    <span className="flex items-center gap-2">
                      <span
                        className={cn(
                          "w-2 h-2 rounded-full",
                          SUBJECT_DOT_COLORS[subj]
                        )}
                      />
                      {subj}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title / Topic</Label>
            <Input
              id="title"
              placeholder="e.g., Calendar Math, Spelling Test"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime" className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Start Time
              </Label>
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime" className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                End Time
              </Label>
              <Input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <Separator />

          {/* Curriculum Outcomes */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1">
                <Target className="h-3.5 w-3.5" />
                Curriculum Outcomes
              </Label>
              <Popover open={outcomePickerOpen} onOpenChange={setOutcomePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Outcome
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-3" align="end">
                  <OutcomePicker
                    outcomes={outcomes}
                    selectedIds={selectedOutcomeIds}
                    onToggle={handleOutcomeToggle}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Selected outcomes display */}
            {selectedOutcomes.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedOutcomes.map((outcome) => (
                  <Badge
                    key={outcome.id}
                    variant="secondary"
                    className="pr-1 flex items-center gap-1"
                  >
                    <span className="truncate max-w-[150px]" title={outcome.outcome_text}>
                      {outcome.id}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => handleRemoveOutcome(outcome.id)}
                      aria-label={`Remove ${outcome.id}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No outcomes selected. Click "Add Outcome" to tag this block with curriculum outcomes.
              </p>
            )}
          </div>

          <Separator />

          {/* Resources (placeholder for future) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" />
              Resources
            </Label>
            <div className="border border-dashed rounded-md p-4 text-center text-sm text-muted-foreground">
              <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Resource attachment coming soon.
              <br />
              You'll be able to attach worksheets and PDFs here.
            </div>
          </div>

          <Separator />

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes for this time block..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {!isNewBlock && onDelete && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="sm:mr-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            {isNewBlock ? "Add Block" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
